import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export default function Dropdown({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const getLabel = (opt) => (typeof opt === "object" ? opt.label : opt);
  const getValue = (opt) => (typeof opt === "object" ? opt.value : opt);

  const selectedOption = options.find(
    (opt) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      String(getValue(opt)) === String(value)
  );

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return; // not laid out yet — skip, retry later
    setCoords({
      top: rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
      width: rect.width
    });
  }, []);

  // Recompute as a safety net whenever `open` flips (covers resize/late layout)
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) updatePosition(); // compute BEFORE opening — btnRef is guaranteed valid here
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      const clickedButton = wrapperRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);
      if (!clickedButton && !clickedMenu) setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    // Capture phase so a descendant's stopPropagation() can't swallow this
    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <div className={`custom-dropdown ${className}`} ref={wrapperRef}>
      <button
        type="button"
        ref={btnRef}
        className={`dropdown-btn ${open ? "open" : ""}`}
        onClick={handleToggle}
      >
        {selectedOption ? getLabel(selectedOption) : placeholder}
        <span>▾</span>
      </button>

      {open &&
        createPortal(
          <div
            className="dropdown-menu"
            ref={menuRef}
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              width: coords.width
            }}
          >
            {options.map((opt, i) => (
              <div
                key={i}
                className={
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  String(getValue(opt)) === String(value)
                    ? "selected"
                    : ""
                }
                onClick={() => {
                  onChange(getValue(opt));
                  setOpen(false);
                }}
              >
                {getLabel(opt)}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}